import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "col-lg-10 mx-auto" }
const _hoisted_2 = { class: "text-center mb-4" }
const _hoisted_3 = {
  key: 0,
  class: "row"
}
const _hoisted_4 = { class: "row" }
const _hoisted_5 = { class: "col-md-12" }
const _hoisted_6 = { class: "row" }
const _hoisted_7 = { class: "col-lg-6" }
const _hoisted_8 = { class: "col-lg-6" }
const _hoisted_9 = { class: "row" }
const _hoisted_10 = { class: "col-lg-6" }
const _hoisted_11 = { class: "col-lg-6" }
const _hoisted_12 = { class: "row my-4" }
const _hoisted_13 = { class: "col" }
const _hoisted_14 = { class: "text-center" }

import { ref, computed, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import View from "../ts/view";
import ScenarioType from "../ts/scenarioType";
import ScenarioOutcome from "../ts/scenarioOutcome";
import HelpText from "@/guiide/ts/helpText";
import { workflow } from "../ts/workflow/workflow";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import EverythingYouWant from "@/guiide/components/design/EverythingYouWant.vue";
import Scenario from "@/guiide/components/scenarios/Scenario.vue";
import AllScenariosGuaranteedIncome from "@/guiide/components/scenarios/AllScenariosGuaranteedIncome.vue";
import ContactUs from "./common/ContactUs.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import Help from "@/guiide/components/common/Help.vue";
import ShortfallSolver from "@/guiide/components/scenarios/ShortfallSolver.vue";
import Constants from "../ts/constants";
import { numberToWord } from "../ts/utilities";

type FocusKey = ScenarioType | "HowWeCalcTestsHelp" | "NavNextButton";


export default /*@__PURE__*/_defineComponent({
  __name: 'Test',
  setup(__props) {

const store = useStore();
const userJustSubscribedToGuaranteedIncome = ref(false);
const lowerGrowthScenario = ref(null);
const liveLongerScenario = ref(null);
const potsFallScenario = ref(null);
const careCostsScenario = ref(null);
const shortfallSolver = ref(null);
const howWeCalculateTestsHelp = ref(null);
const pageNav = ref(null);
const nextLink = ref("");
const focusables = new Map<FocusKey, any>();

onMounted(async () => {
  const nextWorkflowStep = await workflow.getNextWorkflowStep(View.Test);
  nextLink.value = nextWorkflowStep.resource;
  initialiseFocusablesMap();
});

const showGuaranteedIncomeDialog = computed<boolean>(() => {
  if (!store.state.configuration.guaranteedIncomeEnabled || !store.state.configuration.testDisplaysGuaranteedIncome) {
    return false;
  }

  if (userJustSubscribedToGuaranteedIncome.value) {
    return true;
  }

  if (!hasPensionPots.value) {
    return false;
  }

  const userSubscribedToGI = store.state.retiree?.guaranteedIncome.subscribed ?? false;
  const someTestsFailed = haveAnyTestsOfInterestFailed.value;
  return !userSubscribedToGI && someTestsFailed;
});

const haveAnyTestsOfInterestFailed = computed<boolean>(() => {
  return (
    store.state.youLiveLongerScenario.outcome ==
    ScenarioOutcome.Fail ||
    store.state.marketCrashScenario.outcome == ScenarioOutcome.Fail ||
    store.state.lowerGrowthScenario.outcome == ScenarioOutcome.Fail
  );
});

const hasPensionPots = computed<boolean>(() => {
  return store.state.retiree?.pensionPots.pots.length > 0;
});

const howWeCalcTestsText = computed<string[]>(() => {
  let text = HelpText.howWeCalcTests();
  const extraYearsForLiveLongerTest = store.state.configuration.defaultExtraYearsForLiveLongerTest;
  const extraYearsWord = numberToWord(extraYearsForLiveLongerTest);

  for (let i = 0; i < text.length; i++) {
    text[i] = text[i].replace(Constants.HelpTextTokens.numberOfExtraYearsInLiveLongerTest, extraYearsWord);
  }

  return text;
});

function onSolveTakeLess(scenarioType: ScenarioType) {
  shortfallSolver.value.checkCouldSolveByTakeLess(scenarioType);
}

function onSolvePayMore(scenarioType: ScenarioType) {
  shortfallSolver.value.checkCouldSolveByPayingMore(scenarioType);
}

function onSolveEquityRelease(scenarioType: ScenarioType) {
  shortfallSolver.value.checkCouldSolveByEquityRelease(scenarioType);
}

function onSubscribedToGuaranteedIncome() {
  userJustSubscribedToGuaranteedIncome.value = true;
}

function onShortfallSolverClosing(eventArgs: { scenario: ScenarioType, implemented: boolean }) {
  if (!eventArgs.implemented) {
    return;
  }

  nextTick(() => {
    const focusKeys = getFocusFallbackKeys(eventArgs.scenario);
    for (const key of focusKeys) {
      const focusable = focusables.get(key);
      const tookFocus: boolean = focusable.takeFocus("");
      if (tookFocus) {
        return;
      }
    }
  });
}

async function onNextClicked() {
  await workflow.goToNextWorkflowStep();
}

function getFocusFallbackKeys(current: FocusKey): FocusKey[] {
  const order: FocusKey[] = [ 
    ScenarioType.LowerGrowth, 
    ScenarioType.LiveThreeYearsLonger, 
    ScenarioType.PotsFallTwentyPercent, 
    ScenarioType.NeedExtraMoneyForLastTwoYears, 
    "HowWeCalcTestsHelp",
    "NavNextButton"
  ];
  const index = order.indexOf(current);
  return index >= 0 ? order.slice(index) : [];
}

function initialiseFocusablesMap() {
  focusables.set(ScenarioType.LowerGrowth, lowerGrowthScenario.value);
  focusables.set(ScenarioType.LiveThreeYearsLonger, liveLongerScenario.value);
  focusables.set(ScenarioType.PotsFallTwentyPercent, potsFallScenario.value);
  focusables.set(ScenarioType.NeedExtraMoneyForLastTwoYears, liveLongerScenario.value);
  focusables.set("HowWeCalcTestsHelp", howWeCalculateTestsHelp.value);
  focusables.set("NavNextButton", pageNav.value);
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("header", _hoisted_2, [
        _createVNode(WorkflowNavigation, { workflowStage: "Test" }),
        _cache[0] || (_cache[0] = _createElementVNode("h2", { class: "skin-page-heading" }, " Let's see what happens when things don't go to plan ", -1)),
        _cache[1] || (_cache[1] = _createElementVNode("p", { class: "col-sm-8 mx-auto" }, " See if it still works if things don't go to plan, if anything is a concern, press solve, if not just leave it. ", -1))
      ]),
      _createVNode(EverythingYouWant),
      (showGuaranteedIncomeDialog.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _cache[2] || (_cache[2] = _createElementVNode("div", { class: "col" }, null, -1)),
            _createElementVNode("div", null, [
              _createVNode(AllScenariosGuaranteedIncome, { onSubscribedToOffer: onSubscribedToGuaranteedIncome })
            ]),
            _cache[3] || (_cache[3] = _createElementVNode("div", { class: "col" }, null, -1))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createVNode(Scenario, {
                ref_key: "lowerGrowthScenario",
                ref: lowerGrowthScenario,
                scenario: _unref(ScenarioType).LowerGrowth,
                number: "1",
                solveEnabled: true,
                onSolveTakeLess: onSolveTakeLess,
                onSolvePayMore: onSolvePayMore,
                onSolveEquityRelease: onSolveEquityRelease
              }, null, 8, ["scenario"])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(Scenario, {
                ref_key: "liveLongerScenario",
                ref: liveLongerScenario,
                scenario: _unref(ScenarioType).LiveThreeYearsLonger,
                number: "2",
                solveEnabled: true,
                onSolveTakeLess: onSolveTakeLess,
                onSolvePayMore: onSolvePayMore,
                onSolveEquityRelease: onSolveEquityRelease
              }, null, 8, ["scenario"])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createVNode(Scenario, {
                ref_key: "potsFallScenario",
                ref: potsFallScenario,
                scenario: _unref(ScenarioType).PotsFallTwentyPercent,
                number: "3",
                solveEnabled: true,
                onSolveTakeLess: onSolveTakeLess,
                onSolvePayMore: onSolvePayMore,
                onSolveEquityRelease: onSolveEquityRelease
              }, null, 8, ["scenario"])
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createVNode(Scenario, {
                ref_key: "careCostsScenario",
                ref: careCostsScenario,
                scenario: _unref(ScenarioType).NeedExtraMoneyForLastTwoYears,
                number: "4",
                solveEnabled: true,
                onSolveTakeLess: onSolveTakeLess,
                onSolvePayMore: onSolvePayMore,
                onSolveEquityRelease: onSolveEquityRelease
              }, null, 8, ["scenario"])
            ])
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("p", _hoisted_14, [
            _cache[4] || (_cache[4] = _createTextVNode(" Find out more detail on how we calculate your tests. ")),
            _createVNode(Help, {
              ref_key: "howWeCalculateTestsHelp",
              ref: howWeCalculateTestsHelp,
              title: "How we calculate your tests",
              text: howWeCalcTestsText.value,
              isWide: true
            }, null, 8, ["text"])
          ])
        ])
      ]),
      _createVNode(PageNav, {
        ref_key: "pageNav",
        ref: pageNav,
        backLink: "design",
        onNextClicked: onNextClicked,
        "parent-will-handle-next": true,
        "next-link": nextLink.value
      }, null, 8, ["next-link"]),
      _createVNode(ContactUs, { class: "mt-5" })
    ]),
    _createVNode(ShortfallSolver, {
      ref_key: "shortfallSolver",
      ref: shortfallSolver,
      onClosing: onShortfallSolverClosing
    }, null, 512)
  ], 64))
}
}

})